import React, { useEffect, useMemo, useState } from 'react';
import { WxbButton, WxbEmpty, WxbModal, WxbTreeSelect, WxbTag } from '../wxb-ui';
import type { ResourceNode } from '../ProcessTemplateV2/types';
import { buildBindingTree, flattenNodes } from './QuickCreateOperationModal';
import './QuickCreateOperationModal.css';

/**
 * 并占 (co-occupancy) 设备多选弹窗 —— 供工艺模版编辑器的「右键绑定设备」与「批量绑定」复用,
 * 让这两个入口也能给操作配置多台同时占用设备,而不只是单台。
 * 与「编辑操作」弹窗用同一套设备树(buildBindingTree)和已选清单样式,
 * 保证措辞/交互一致。返回值仍保留 primaryNodeId 以兼容存储结构;UI 不暴露主次关系。
 */
export interface DeviceCoUseBindModalProps {
  open: boolean;
  title: string;
  /** 可选副标题(如批量时说明会作用于几个操作)。 */
  subtitle?: string;
  /** 资源节点树(厂区→产线→房间→设备),与编辑弹窗同源 resourceTree。 */
  resourceNodes: ResourceNode[];
  /** 预填:存储兼容字段中的第一台设备(单操作编辑时传入;批量时一般为 null)。 */
  initialPrimaryId?: number | null;
  /** 预填:当前其他同时占用设备。 */
  initialCandidateIds?: number[];
  confirmText?: string;
  confirmLoading?: boolean;
  onCancel: () => void;
  onConfirm: (primaryNodeId: number | null, candidateNodeIds: number[]) => void | Promise<void>;
}

const DeviceCoUseBindModal: React.FC<DeviceCoUseBindModalProps> = ({
  open,
  title,
  subtitle,
  resourceNodes,
  initialPrimaryId = null,
  initialCandidateIds = [],
  confirmText = '保存',
  confirmLoading = false,
  onCancel,
  onConfirm,
}) => {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  // Re-seed state every time the modal opens (or its prefill target changes), so reopening
  // for a different operation / selection starts from that operation's current bindings.
  const initialKey = `${initialPrimaryId ?? ''}:${(initialCandidateIds ?? []).join(',')}`;
  useEffect(() => {
    if (!open) return;
    const primary = initialPrimaryId ?? null;
    const candidates = (initialCandidateIds ?? []).filter((id) => id !== primary);
    const ordered = primary != null ? [primary, ...candidates] : candidates;
    setSelectedIds(ordered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKey]);

  const { treeData, expandedKeys } = useMemo(() => buildBindingTree(resourceNodes), [resourceNodes]);

  const labelById = useMemo(() => {
    const map = new Map<number, string>();
    for (const node of flattenNodes(resourceNodes)) {
      const extra =
        node.nodeClass === 'EQUIPMENT_UNIT'
          ? [node.equipmentSystemType, node.equipmentClass].filter(Boolean).join(' · ')
          : '';
      map.set(Number(node.id), extra ? `${node.nodeName}（${extra}）` : node.nodeName);
    }
    return map;
  }, [resourceNodes]);

  // grp-* group nodes are non-checkable strings; Number() → NaN, so they never enter the set.
  const handleChange = (value: Array<number | string>) => {
    const nextIds = (value ?? [])
      .map((v) => Number(v))
      .filter((v) => Number.isInteger(v) && v > 0);
    setSelectedIds(nextIds);
  };

  const handleConfirm = () => {
    const [storageLeadId, ...coOccupiedIds] = selectedIds;
    void onConfirm(storageLeadId ?? null, coOccupiedIds);
  };

  const selectedCountLabel = selectedIds.length > 0 ? `已选 ${selectedIds.length} 台` : '未选择设备';

  return (
    <WxbModal
      open={open}
      title={title}
      className="device-co-use-bind-modal"
      okText={confirmText}
      cancelText="取消"
      confirmLoading={confirmLoading}
      onOk={handleConfirm}
      onCancel={onCancel}
      width={560}
      destroyOnClose
    >
      <div className="qcom-multibind qcom-multibind-dialog">
        {subtitle ? (
          <div className="qcom-multibind-context">
            <span className="qcom-multibind-context-label">配置对象</span>
            <span className="qcom-multibind-context-name">{subtitle}</span>
          </div>
        ) : null}
        <div className="qcom-multibind-bar">
          <div className="qcom-multibind-tags">
            <WxbTag color="blue">同时占用</WxbTag>
            <WxbTag color="neutral">同级必需</WxbTag>
          </div>
          <span className="qcom-multibind-count">{selectedCountLabel}</span>
        </div>
        <WxbTreeSelect
          label="占用设备"
          treeData={treeData}
          value={selectedIds}
          treeCheckable
          showSearch
          allowClear
          treeNodeFilterProp="title"
          treeDefaultExpandedKeys={expandedKeys}
          popupMatchSelectWidth={false}
          popupClassName="qcom-bind-popup"
          listHeight={360}
          maxTagCount={0}
          maxTagPlaceholder={() => selectedCountLabel}
          placeholder="选择本操作同时占用的设备"
          onChange={(value) => handleChange((value as Array<number | string>) ?? [])}
        />
        <div className="qcom-multibind-selected">
          <div className="qcom-multibind-selected-head">
            <span>已选设备</span>
            {selectedIds.length > 0 ? (
              <WxbButton type="button" variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                清空
              </WxbButton>
            ) : null}
          </div>
          {selectedIds.length > 0 ? (
            <ul className="qcom-multibind-list">
              {selectedIds.map((nodeId) => (
                <li key={nodeId} className="qcom-multibind-item">
                  <span className="qcom-multibind-name">{labelById.get(nodeId) ?? `设备 #${nodeId}`}</span>
                  <WxbTag color="blue">占用</WxbTag>
                </li>
              ))}
            </ul>
          ) : (
            <WxbEmpty
              className="qcom-multibind-empty"
              image={<span className="qcom-multibind-empty-icon" aria-hidden="true" />}
              description="未选择设备，保存后将解除设备绑定"
            />
          )}
        </div>
      </div>
    </WxbModal>
  );
};

export default DeviceCoUseBindModal;
